import React from "react";
import { useFinanceConfig } from "../../context/FinanceConfigContext";
import type { PriceBook } from "../../context/FinanceConfigContext";

// ─── Types ────────────────────────────────────────────────────────────────────

type TechFeature = "HEAT-RESISTANT" | "STANDARD" | "HEAVY-DUTY";
type StorageLocation = "MAIN STORAGE" | "ON-SITE STOCK" | "MAINTENANCE ROOM" | "SPARE PART STORAGE";

export interface PartDetail {
  id: string;
  articleNumber: string;
  name: string;
  description: string;
  availableStock: number | null;
  consumedStock: number | null;
  technicalFeatures: TechFeature | null;
  storageLocation: StorageLocation | null;
}

interface PartDetailDrawerProps {
  open: boolean;
  part: PartDetail | null;
  onClose: () => void;
}

type Tab = "Details" | "Stock" | "Requests" | "Activity Log" | "Documentation";
const TABS: Tab[] = ["Details", "Stock", "Requests", "Activity Log", "Documentation"];

// ─── Icons ────────────────────────────────────────────────────────────────────

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--color-content-secondary)" strokeWidth="1.6" strokeLinecap="round">
      <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
    </svg>
  );
}

function ChevronUpIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      width="20" height="20" viewBox="0 0 20 20" fill="none"
      stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transition: "transform 180ms ease", transform: collapsed ? "rotate(180deg)" : "rotate(0deg)" }}
    >
      <path d="M5 12.5l5-5 5 5" />
    </svg>
  );
}

function SingleSelectIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.3" strokeLinecap="round">
      <circle cx="8" cy="8" r="6" />
      <circle cx="8" cy="8" r="2.5" fill="#748094" />
    </svg>
  );
}

function MultiSelectIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 5l2.5 2.5L9 4" />
      <path d="M2.5 11l2.5 2.5L9 10" />
      <path d="M12 5.5h1.5M12 11.5h1.5" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#748094" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3.5h10M5 3.5V2.5a.5.5 0 01.5-.5h3a.5.5 0 01.5.5v1M11.5 3.5l-.8 8a1 1 0 01-1 .9H4.3a1 1 0 01-1-.9l-.8-8" />
      <path d="M5.5 6.5v3.5M8.5 6.5v3.5" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#748094" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13V5M7 8l3-3 3 3" />
      <path d="M4 14v1.5A1.5 1.5 0 005.5 17h9a1.5 1.5 0 001.5-1.5V14" />
    </svg>
  );
}

// ─── Parts Image Placeholder ──────────────────────────────────────────────────

function PartImagePlaceholder() {
  return (
    <div style={{
      width: 120, height: 120,
      borderRadius: 8,
      border: "1px solid var(--color-border-primary)",
      background: "#f8f9fa",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    }}>
      <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
        <circle cx="32" cy="32" r="18" stroke="#94a3b8" strokeWidth="3" fill="none" />
        <circle cx="32" cy="32" r="9" stroke="#64748b" strokeWidth="3" fill="none" />
        <circle cx="32" cy="32" r="3.5" fill="#475569" />
        <circle cx="32" cy="14.5" r="3" fill="#94a3b8" />
        <circle cx="32" cy="49.5" r="3" fill="#94a3b8" />
        <circle cx="14.5" cy="32" r="3" fill="#94a3b8" />
        <circle cx="49.5" cy="32" r="3" fill="#94a3b8" />
        <circle cx="19.8" cy="19.8" r="2" fill="#94a3b8" />
        <circle cx="44.2" cy="44.2" r="2" fill="#94a3b8" />
        <circle cx="44.2" cy="19.8" r="2" fill="#94a3b8" />
        <circle cx="19.8" cy="44.2" r="2" fill="#94a3b8" />
      </svg>
    </div>
  );
}

function PlusIcon({ color = "#0517f8" }: { color?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
      <path d="M7 2.5v9M2.5 7h9" />
    </svg>
  );
}

function ChevronToggleIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16" height="16" viewBox="0 0 16 16" fill="none"
      stroke="#748094" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transition: "transform 200ms", transform: open ? "rotate(0deg)" : "rotate(180deg)" }}
    >
      <path d="M4 10l4-4 4 4" />
    </svg>
  );
}

function RemoveIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#D1D5DB" strokeWidth="1.4" strokeLinecap="round">
      <circle cx="9" cy="9" r="7.5" />
      <path d="M6 9h6" />
    </svg>
  );
}

function PriceBookSectionIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#0517f8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3.5 1.5h6l3 3v9.5a.5.5 0 01-.5.5h-8.5a.5.5 0 01-.5-.5v-12a.5.5 0 01.5-.5z" />
      <path d="M9.5 1.5v3h3" />
      <path d="M5.5 7h5M5.5 9.5h3.5" />
    </svg>
  );
}

// ─── Price Book Section ───────────────────────────────────────────────────────

const CURRENCY_SYMBOL: Record<string, string> = {
  EUR: "€", USD: "$", GBP: "£", JPY: "¥", CHF: "Fr",
  AUD: "A$", CAD: "C$", SEK: "kr", NOK: "kr", DKK: "kr",
  PLN: "zł", CZK: "Kč", HUF: "Ft", RON: "lei", BGN: "лв",
  HRK: "kn", RUB: "₽", TRY: "₺", INR: "₹", CNY: "¥",
  BRL: "R$", MXN: "$",
};

function currencySymbol(code: string): string {
  return CURRENCY_SYMBOL[code] ?? code;
}

interface PriceBookItem {
  id: string;
  priceBookId: string;
  priceBookName: string;
  currency: string;
  price: string;
  discount: string;
}

function PriceBookSection() {
  const { config: financeConfig } = useFinanceConfig();
  const [open, setOpen] = React.useState(true);
  const [items, setItems] = React.useState<PriceBookItem[]>([]);
  const [showForm, setShowForm] = React.useState(false);
  const [selectedPriceBookId, setSelectedPriceBookId] = React.useState("");
  const [price, setPrice] = React.useState("");
  const [discount, setDiscount] = React.useState("");

  function handleAdd() {
    const pb = financeConfig.priceBooks.find((p: PriceBook) => p.id === selectedPriceBookId);
    if (!pb || !price) return;
    setItems(prev => [...prev, {
      id: `pbi-${Date.now()}`,
      priceBookId: pb.id,
      priceBookName: pb.name,
      currency: pb.currency,
      price,
      discount: discount || "0",
    }]);
    setSelectedPriceBookId("");
    setPrice("");
    setDiscount("");
    setShowForm(false);
  }

  function handleCancel() {
    setSelectedPriceBookId("");
    setPrice("");
    setDiscount("");
    setShowForm(false);
  }

  return (
    <div style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 8 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px" }}>
        <div style={{
          width: 24, height: 24, borderRadius: "50%", background: "#EEF0FF",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <PriceBookSectionIcon />
        </div>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
          Price Book
        </span>
        <button
          onClick={() => { setShowForm(true); if (!open) setOpen(true); }}
          style={{
            display: "flex", alignItems: "center", gap: 4,
            border: "none", background: "transparent", cursor: "pointer", padding: 4,
            fontSize: 12, fontWeight: 700, color: "#0517f8", fontFamily: "var(--font-family-base)",
            letterSpacing: "-0.24px",
          }}
        >
          <PlusIcon color="#0517f8" />
          Add
        </button>
        <div
          style={{ cursor: "pointer", flexShrink: 0 }}
          onClick={() => setOpen(v => !v)}
        >
          <ChevronToggleIcon open={open} />
        </div>
      </div>

      {open && (items.length > 0 || showForm) && (
        <div style={{ borderTop: "1px solid #E5E7EB" }}>
          {/* Table header */}
          {items.length > 0 && (
            <>
              <div style={{ display: "flex", background: "#F9FAFB", padding: "0 16px" }}>
                <div style={{ flex: 1, padding: "8px 0" }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>Price Book</span>
                </div>
                <div style={{ width: 120, padding: "8px 0" }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>Price</span>
                </div>
                <div style={{ width: 100, padding: "8px 0" }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>Discount</span>
                </div>
                <div style={{ width: 36 }} />
              </div>
              {items.map(item => (
                <div key={item.id} style={{ display: "flex", alignItems: "center", padding: "0 16px", borderTop: "1px solid #E5E7EB" }}>
                  <div style={{ flex: 1, padding: "12px 0" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: "var(--font-family-base)", display: "block", letterSpacing: "-0.24px" }}>
                      {item.priceBookName}
                    </span>
                  </div>
                  <div style={{ width: 120 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: "#374151", fontFamily: "var(--font-family-base)" }}>
                      {currencySymbol(item.currency)}{parseFloat(item.price).toLocaleString("en", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div style={{ width: 100 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: "#374151", fontFamily: "var(--font-family-base)" }}>
                      {item.discount}%
                    </span>
                  </div>
                  <div style={{ width: 36, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <button
                      onClick={() => setItems(prev => prev.filter(i => i.id !== item.id))}
                      style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex" }}
                    >
                      <RemoveIcon />
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Inline add form */}
          {showForm && (
            <div style={{ padding: "14px 16px", borderTop: items.length > 0 ? "1px solid #E5E7EB" : undefined, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", fontFamily: "var(--font-family-base)", letterSpacing: "0.2px" }}>
                  Price Book <span style={{ color: "#EF4444" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <select
                    value={selectedPriceBookId}
                    onChange={e => setSelectedPriceBookId(e.target.value)}
                    style={{
                      width: "100%", height: 34, padding: "0 28px 0 10px",
                      border: "1px solid #E5E7EB", borderRadius: 6,
                      background: "white", appearance: "none",
                      fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 500,
                      color: selectedPriceBookId ? "#111827" : "#9CA3AF", outline: "none", cursor: "pointer",
                      boxSizing: "border-box",
                    }}
                  >
                    <option value="" disabled>Select price book</option>
                    {financeConfig.priceBooks.map((pb: PriceBook) => (
                      <option key={pb.id} value={pb.id}>{pb.name} ({pb.currency})</option>
                    ))}
                  </select>
                  <svg style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#6B7280" strokeWidth="1.4" strokeLinecap="round"><path d="M2 3.5l3 3 3-3" /></svg>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", fontFamily: "var(--font-family-base)", letterSpacing: "0.2px" }}>
                    Price <span style={{ color: "#EF4444" }}>*</span>
                  </label>
                  <input
                    type="number" min="0" placeholder="0.00"
                    value={price} onChange={e => setPrice(e.target.value)}
                    style={{ height: 34, padding: "0 10px", border: "1px solid #E5E7EB", borderRadius: 6, fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 500, color: "#111827", outline: "none", boxSizing: "border-box", width: "100%" }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", fontFamily: "var(--font-family-base)", letterSpacing: "0.2px" }}>
                    Discount (%)
                  </label>
                  <input
                    type="number" min="0" max="100" placeholder="0"
                    value={discount} onChange={e => setDiscount(e.target.value)}
                    style={{ height: 34, padding: "0 10px", border: "1px solid #E5E7EB", borderRadius: 6, fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 500, color: "#111827", outline: "none", boxSizing: "border-box", width: "100%" }}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={handleCancel} style={{ height: 30, padding: "0 12px", border: "1px solid #E5E7EB", borderRadius: 6, background: "white", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#374151", fontFamily: "var(--font-family-base)" }}>
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  disabled={!selectedPriceBookId || !price}
                  style={{ height: 30, padding: "0 12px", border: "none", borderRadius: 6, background: !selectedPriceBookId || !price ? "#E5E7EB" : "#0517f8", cursor: !selectedPriceBookId || !price ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600, color: !selectedPriceBookId || !price ? "#9CA3AF" : "white", fontFamily: "var(--font-family-base)" }}
                >
                  Add
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Field ────────────────────────────────────────────────────────────────────

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
      <div style={{ display: "flex", gap: 4, fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", lineHeight: "16px" }}>
        <span>{label}</span>
        {required && <span style={{ color: "var(--color-content-secondary)" }}>*</span>}
      </div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "12px",
  border: "1px solid #e8eaed",
  borderRadius: 8,
  fontFamily: "var(--font-family-base)",
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-content-primary)",
  lineHeight: "16px",
  background: "var(--color-bg-primary)",
  outline: "none",
  boxSizing: "border-box",
  resize: "none",
};

// ─── Details Tab Content ──────────────────────────────────────────────────────

function DetailsTab({ part }: { part: PartDetail }) {
  const { config: financeConfig } = useFinanceConfig();
  const symbol = currencySymbol(financeConfig.currency);
  const [name, setName] = React.useState(part.name);
  const [articleNumber, setArticleNumber] = React.useState(part.articleNumber);
  const [description, setDescription] = React.useState(part.description);
  const [basePrice, setBasePrice] = React.useState("");
  const [customFieldsCollapsed, setCustomFieldsCollapsed] = React.useState(false);

  React.useEffect(() => {
    setName(part.name);
    setArticleNumber(part.articleNumber);
    setDescription(part.description);
  }, [part]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>
      {/* Part Name */}
      <Field label="Part Name" required>
        <input
          style={inputStyle}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>

      {/* Article Number */}
      <Field label="Article Number" required>
        <input
          style={inputStyle}
          value={articleNumber}
          onChange={(e) => setArticleNumber(e.target.value)}
        />
      </Field>

      {/* Description */}
      <Field label="Description">
        <textarea
          style={{ ...inputStyle, minHeight: 96 }}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>

      {/* Price */}
      <Field label="Price">
        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
          <span style={{
            position: "absolute", left: 12,
            fontSize: 12, fontWeight: 600,
            color: "var(--color-content-secondary)",
            fontFamily: "var(--font-family-base)",
            pointerEvents: "none",
            userSelect: "none",
          }}>
            {symbol}
          </span>
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={basePrice}
            onChange={e => setBasePrice(e.target.value)}
            style={{ ...inputStyle, paddingLeft: symbol.length > 1 ? 28 : 24 }}
          />
        </div>
      </Field>

      {/* Upload Image */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={{ fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", lineHeight: "16px" }}>
          Upload Image
        </span>
        <div style={{ position: "relative", display: "inline-flex" }}>
          <PartImagePlaceholder />
          {/* Delete overlay button */}
          <button style={{
            position: "absolute",
            bottom: 8, right: 8,
            width: 28, height: 28,
            background: "var(--color-bg-primary)",
            border: "1px solid var(--color-border-primary)",
            borderRadius: 6,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
          }}>
            <TrashIcon />
          </button>
        </div>
        {/* Upload button */}
        <button style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "6px 10px",
          background: "transparent",
          border: "1px dashed #d1d5db",
          borderRadius: 8,
          fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 600,
          color: "var(--color-content-secondary)",
          cursor: "pointer",
          width: 120,
          justifyContent: "center",
        }}>
          <UploadIcon />
          Upload
        </button>
      </div>

      {/* Custom Fields */}
      <div style={{
        border: "1px solid var(--color-border-primary)",
        borderRadius: 8,
        padding: 16,
        display: "flex", flexDirection: "column", gap: 16,
        width: "100%", boxSizing: "border-box",
      }}>
        {/* Header */}
        <button
          onClick={() => setCustomFieldsCollapsed((v) => !v)}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "none", border: "none", cursor: "pointer", padding: 0, width: "100%",
          }}
        >
          <span style={{ fontFamily: "var(--font-family-base)", fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)", letterSpacing: "-0.28px", lineHeight: "20px" }}>
            Custom Fields
          </span>
          <ChevronUpIcon collapsed={customFieldsCollapsed} />
        </button>

        {/* Rows */}
        {!customFieldsCollapsed && (
          <>
            <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
              {/* Single Select row */}
              <div style={{ display: "flex", alignItems: "center", width: "100%", paddingTop: 4, paddingBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, width: 140, flexShrink: 0, paddingTop: 8, paddingBottom: 8 }}>
                  <SingleSelectIcon />
                  <span style={{ fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    Single Select
                  </span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    background: "var(--color-bg-primary)",
                    border: "1px solid #e8eaed",
                    borderRadius: 4,
                    height: 32,
                    padding: "0 8px",
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <CustomTag label="CUSTOM" />
                  </div>
                </div>
              </div>

              {/* Multi Select row */}
              <div style={{ display: "flex", alignItems: "center", width: "100%", paddingTop: 4, paddingBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, width: 140, flexShrink: 0, paddingTop: 8, paddingBottom: 8 }}>
                  <MultiSelectIcon />
                  <span style={{ fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    Multi Select
                  </span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    background: "var(--color-bg-primary)",
                    border: "1px solid #e8eaed",
                    borderRadius: 4,
                    height: 32,
                    padding: "0 8px",
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <CustomTag label="CUSTOM" />
                    <CustomTag label="+1" />
                  </div>
                </div>
              </div>
            </div>

            {/* Show Empty Fields */}
            <button style={{
              background: "none", border: "none", cursor: "pointer", padding: "4px 0",
              fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
              color: "var(--color-bg-brand-dark)", letterSpacing: "-0.24px",
            }}>
              Show Empty Fields
            </button>
          </>
        )}
      </div>

      {/* Price Book */}
      <PriceBookSection />
    </div>
  );
}

function CustomTag({ label }: { label: string }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      padding: "4px 6px", borderRadius: 4,
      fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px",
      background: "#f6f7ff", color: "#6974fb",
      fontFamily: "var(--font-family-base)", whiteSpace: "nowrap", lineHeight: "16px",
    }}>
      {label}
    </span>
  );
}

// ─── Empty Tab ────────────────────────────────────────────────────────────────

function EmptyTab({ label }: { label: string }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      gap: 8, padding: "48px 24px", color: "var(--color-content-tertiary)",
      fontFamily: "var(--font-family-base)",
    }}>
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" opacity="0.4">
        <circle cx="20" cy="20" r="18" stroke="#748094" strokeWidth="2" />
        <path d="M20 12v10M20 28v2" stroke="#748094" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{label} — coming soon</span>
    </div>
  );
}

// ─── Drawer ───────────────────────────────────────────────────────────────────

export function PartDetailDrawer({ open, part, onClose }: PartDetailDrawerProps) {
  const [activeTab, setActiveTab] = React.useState<Tab>("Details");

  React.useEffect(() => {
    if (open) setActiveTab("Details");
  }, [open, part?.id]);

  if (!open || !part) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 200 }}
      />

      {/* Drawer */}
      <div style={{
        position: "fixed",
        top: 0, right: 0,
        width: 480,
        height: "100vh",
        background: "var(--color-bg-primary)",
        zIndex: 201,
        display: "flex",
        flexDirection: "column",
        boxShadow: "0px 0px 2px 1px rgba(23,43,77,0.04), 0px 16px 32px 0px rgba(23,43,77,0.12)",
      }}>

        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "20px 24px",
          borderBottom: "1px solid var(--color-border-primary)",
          flexShrink: 0,
        }}>
          <span style={{
            fontFamily: "var(--font-family-base)",
            fontSize: 18, fontWeight: 700,
            color: "var(--color-content-primary)",
            letterSpacing: "-0.36px", lineHeight: "28px",
          }}>
            Part Details
          </span>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32,
              background: "var(--color-bg-primary)",
              border: "1px solid var(--color-border-primary)",
              borderRadius: 8,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 24px" }}>

          {/* Tabs */}
          <div style={{
            display: "flex", alignItems: "flex-end", gap: 0,
            borderBottom: "1px solid var(--color-border-primary)",
            paddingTop: 16, marginBottom: 20,
          }}>
            {TABS.map((tab) => {
              const active = tab === activeTab;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
                    padding: "0 8px",
                    background: "none", border: "none", cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <span style={{
                    fontFamily: "var(--font-family-base)",
                    fontSize: 12, fontWeight: 700,
                    color: active ? "var(--color-content-primary)" : "var(--color-content-tertiary)",
                    letterSpacing: "-0.24px", lineHeight: "16px",
                    whiteSpace: "nowrap",
                  }}>
                    {tab}
                  </span>
                  <div style={{
                    height: 2, width: "100%", borderRadius: "4px 4px 0 0",
                    background: active ? "var(--color-bg-brand-dark)" : "transparent",
                    flexShrink: 0,
                  }} />
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          {activeTab === "Details" && <DetailsTab part={part} />}
          {activeTab === "Stock" && <EmptyTab label="Stock" />}
          {activeTab === "Requests" && <EmptyTab label="Requests" />}
          {activeTab === "Activity Log" && <EmptyTab label="Activity Log" />}
          {activeTab === "Documentation" && <EmptyTab label="Documentation" />}
        </div>

        {/* Footer */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 24px",
          borderTop: "1px solid var(--color-border-primary)",
          flexShrink: 0,
          background: "var(--color-bg-primary)",
        }}>
          {/* Delete */}
          <button style={{
            padding: "10px 12px",
            background: "var(--color-bg-primary)",
            border: "1px solid #ee0064",
            borderRadius: 8,
            fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
            color: "#ee0064", letterSpacing: "-0.24px",
            cursor: "pointer",
          }}>
            Delete Part
          </button>

          {/* Cancel + Save */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={onClose}
              style={{
                padding: "10px 12px",
                background: "var(--color-bg-primary)",
                border: "1px solid var(--color-border-primary)",
                borderRadius: 8,
                fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
                color: "var(--color-content-primary)", letterSpacing: "-0.24px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              style={{
                padding: "10px 12px",
                background: "#d1d5db",
                border: "none",
                borderRadius: 8,
                fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
                color: "#ffffff", letterSpacing: "-0.24px",
                cursor: "not-allowed",
              }}
              disabled
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
